/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useCallback, useMemo } from 'react';
import { getMessageFromError } from 'sarsaparilla/src/utils';
import { useRecaptcha } from './RecaptchaContext';
import type {
    RecaptchaWithFallbackResult,
    RecaptchaRegion,
    RecaptchaErrorResult,
} from './recaptcha.types';

// Error messages that indicate v2 challenge is required
const CHALLENGE_REQUIRED_MESSAGES = [
    'additional challenge required',
    'challenge required',
];

// Error messages that indicate challenge failed
const CHALLENGE_FAILED_MESSAGES = ['challenge failed'];

/**
 * Whether an error message is a reCAPTCHA challenge control-signal
 * (challenge required / challenge failed) rather than a user-facing error.
 *
 * These messages drive the v2 fallback modal, so consumers should filter them
 * out of any error display - the modal is what tells the user what to do.
 */
export function isRecaptchaChallengeMessage(message: unknown): boolean {
    if (typeof message !== 'string') return false;
    return [...CHALLENGE_REQUIRED_MESSAGES, ...CHALLENGE_FAILED_MESSAGES].some((msg) =>
        message.includes(msg)
    );
}

/**
 * useRecaptchaWithFallback
 *
 * Hook that provides isolated fallback state management for reCAPTCHA.
 * Each usage of this hook gets its own independent fallback state,
 * preventing cross-contamination between different forms/components.
 *
 * Consumes the core RecaptchaProvider for script loading and v3 execution,
 * and manages its own isChallengeRequired and v2Token state.
 *
 * @returns Object containing token methods, error handling, and checkbox props
 *
 * @example
 * function LoginForm() {
 *   const {
 *     getToken,
 *     getRecaptchaRegion,
 *     handleRecaptchaError,
 *     recaptchaCheckboxProps,
 *   } = useRecaptchaWithFallback();
 *
 *   const onSubmit = async () => {
 *     try {
 *       const token = await getToken('login');
 *       const region = getRecaptchaRegion();
 *       await submitToAPI({ token, region });
 *     } catch (error) {
 *       const { shouldRetry } = handleRecaptchaError(error);
 *       if (shouldRetry) return; // User will complete checkbox and resubmit
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={onSubmit}>
 *       <RecaptchaCheckbox {...recaptchaCheckboxProps} action="login" />
 *       <button type="submit">Submit</button>
 *     </form>
 *   );
 * }
 */
export function useRecaptchaWithFallback(): RecaptchaWithFallbackResult {
    // Get the core recaptcha context (v3 execution)
    const { executeV3 } = useRecaptcha();

    // Isolated fallback state for this instance
    const [isChallengeRequired, setIsChallengeRequired] = useState(false);
    const [v2Token, setV2Token] = useState<string | null>(null);

    /**
     * Get a recaptcha token for the given action.
     * Returns v3 token if in v3 mode, or v2 token if challenge was required.
     */
    const getToken = useCallback(
        async (action: string): Promise<string | null> => {
            // If v2 challenge is required, return the v2 token
            if (isChallengeRequired) {
                return v2Token;
            }

            return await executeV3(action);
        },
        [isChallengeRequired, v2Token, executeV3]
    );

    /**
     * Get the current recaptcha region for API payloads.
     * Returns 'invisible' for v3, 'WEST' for v2.
     */
    const getRecaptchaRegion = useCallback((): RecaptchaRegion => {
        return isChallengeRequired ? 'WEST' : 'invisible';
    }, [isChallengeRequired]);

    /**
     * Handle a recaptcha-related API error.
     * Automatically switches to v2 mode if challenge is required.
     */
    const handleRecaptchaError = useCallback((error: unknown): RecaptchaErrorResult => {
        const message = getMessageFromError(error);

        const shouldRetry = CHALLENGE_REQUIRED_MESSAGES.some((msg) =>
            message.includes(msg)
        );
        const isFailed = CHALLENGE_FAILED_MESSAGES.some((msg) => message.includes(msg));

        // Automatically switch to v2 mode if challenge is required
        if (shouldRetry) {
            setIsChallengeRequired(true);
        }

        return {
            shouldRetry,
            isFailed,
        };
    }, []);

    /**
     * Reset the challenge state back to v3 mode.
     * Call this when the form is successfully submitted or the user navigates away.
     */
    const resetChallenge = useCallback(() => {
        setIsChallengeRequired(false);
        setV2Token(null);
    }, []);

    /**
     * Callback for when the v2 token changes (passed to RecaptchaCheckbox)
     */
    const onTokenChange = useCallback((token: string | null) => {
        setV2Token(token);
    }, []);

    // Computed validation state: v2 checkbox is required but not completed
    const isV2Invalid = isChallengeRequired && !v2Token;

    // Build the result object
    const result: RecaptchaWithFallbackResult = useMemo(
        () => ({
            // Token & region
            getToken,
            getRecaptchaRegion,

            // Error handling
            handleRecaptchaError,
            resetChallenge,

            // State (for advanced usage)
            isChallengeRequired,
            setIsChallengeRequired,
            v2Token,

            // Validation
            isV2Invalid,

            // Props to spread onto RecaptchaCheckbox
            recaptchaCheckboxProps: {
                isChallengeRequired,
                onTokenChange,
            },
        }),
        [
            getToken,
            getRecaptchaRegion,
            handleRecaptchaError,
            resetChallenge,
            isChallengeRequired,
            v2Token,
            isV2Invalid,
            onTokenChange,
        ]
    );

    return result;
}
